/**
 * React hook — регистрирует HTML-элемент как onboarding anchor.
 *
 * Использование:
 *   const ref = useAnchor('spark');
 *   return <button ref={ref}>...</button>;
 *
 * На unmount автоматически снимает регистрацию.
 */
import { useLayoutEffect, useRef } from 'react';
import { registerAnchor } from './anchors.js';
import type { TipAnchor } from './types.js';

/**
 * 2026-05-26 perf/correctness: useLayoutEffect вместо useEffect.
 * useEffect запускается ПОСЛЕ paint — anchor регистрируется на 1 frame
 * позже первого рендера. Если onboarding tip mount'ится одновременно с
 * anchor компонентом (e.g. CrystallizeButton + crystallize_appeared tip),
 * первый poll backdrop'а/focusRing'а на 250ms может промахнуться по
 * якорю — рендерится fullBackdrop без hole. useLayoutEffect register'ит
 * synchronously после DOM mutation, ДО paint — позиция готова с первого
 * кадра.
 */
export function useAnchor<T extends HTMLElement>(name: TipAnchor): React.RefObject<T> {
  const ref = useRef<T | null>(null);

  useLayoutEffect(() => {
    if (ref.current !== null) {
      registerAnchor(name, ref.current);
    }
    return () => {
      registerAnchor(name, null);
    };
  }, [name]);

  return ref;
}
